"use client";

import { cn } from "@/lib/utils";

import { useValueFormat } from "../../hooks/use-value-format";
import { WidgetEmpty } from "../widget-card";

import type { GaugeItem, GaugePayload } from "@/types";

// The arc is drawn as discrete tapered spokes rather than a stroked path, which
// is what gives the BURO gauge its look. 13 is odd on purpose: it puts one spoke
// exactly at the top, so a half-full gauge reads as half rather than ambiguously
// straddling the centre.
const SPOKES = 13;

// Geometry, in the SVG's own units. The viewBox is a half-square plus a little
// vertical room, so the arc sits flush and the box carries no dead space.
const CENTRE_X = 100;
const CENTRE_Y = 100;
// 86 rather than 90 because the outer cap below bulges past this radius by its own
// half-width; the arc's real extent is OUTER_R + that, and it has to stay inside
// the viewBox.
const OUTER_R = 86;

// The band is deliberately deep — roughly half the radius — because that ratio is
// what drives the TAPER. A spoke spans an angle, so its edge widths grow with
// radius: outer/inner here is 86/48 ≈ 1.8, which is what makes each wedge visibly
// narrower at the inner end. The first attempt used 60, giving 1.5, and the wedges
// came out nearly parallel-sided — they stopped reading as spokes at all.
const INNER_R = 48;

// Half the angular width of a spoke, in degrees — so spokes are 9.8° wide, and
// with the pitch below that leaves a 4.4° gap between neighbours.
//
// An earlier version put this at 5.5 and rounded the corners with a stroke, which
// inflated every wedge and left the gaps hairline; the gap has to be budgeted
// after whatever does the rounding, not before it. The per-end caps used now grow
// the shape only radially, so this angle IS the spacing.
const SPOKE_HALF_ANGLE = 4.9;

// Each end is capped with a circle centred on that edge's midpoint, radius = that
// edge's own half-width, which turns the wedge into a tapered pill.
//
// This replaced a round-join STROKE. A stroke was less code but wrong twice over:
// a constant width can only ever be half-rounding on the wide end while
// over-rounding the narrow one, and — because it inflates every side equally — it
// both ate the gaps and diluted the very taper the wedges exist to show. A cap
// scaled per end rounds both fully and grows the shape only RADIALLY, so the gaps
// between neighbours are untouched.
const capRadius = (radius: number) =>
  radius * Math.sin((SPOKE_HALF_ANGLE * Math.PI) / 180);

/**
 * One or two progress arcs in a single card.
 *
 * Both arcs live in ONE widget rather than two cards side by side: a saved layout
 * is a flat list of keys with no notion of a group, so two cards could never be
 * kept adjacent or dragged as a unit. See GrossProfitTargetsWidget.
 */
export function GaugeWidget({ payload }: { payload: GaugePayload }) {
  if (payload.empty_text || payload.items.length === 0) {
    return <WidgetEmpty label={payload.empty_text ?? ""} />;
  }

  return (
    // One arc takes the whole row; two share it, stacking on mobile where a pair
    // of side-by-side arcs would each be too small to read. items-stretch keeps
    // both columns the same height so the two baselines line up.
    <div
      className={cn(
        "grid flex-1 items-stretch gap-4",
        payload.items.length > 1 && "sm:grid-cols-2",
      )}
    >
      {payload.items.map((item) => (
        <Gauge key={item.label} item={item} />
      ))}
    </div>
  );
}

/**
 * A single labelled arc, as the design system's segmented half-circle.
 *
 * Three figures, each answering a different question: the arc and its percentage
 * say how far along, the headline says where the driver actually IS, and the scale
 * beneath says where zero and the target sit. All in the same format, so they can
 * be read against each other.
 *
 * Spokes fill in proportion to `percent`, to the nearest spoke so early progress
 * shows, but the last fills only at 100%: a full gauge means the target is reached.
 *
 * `percent` comes from the server already computed — see GaugePayloadData for why
 * the division is not done here.
 */
function Gauge({ item }: { item: GaugeItem }) {
  const format = useValueFormat();

  // Clamped for the ARC only. The figures below still report the real number, so
  // a driver at 140% sees a full gauge and the true percentage.
  const clamped = Math.min(Math.max(item.percent, 0), 100);
  const filled =
    clamped >= 100
      ? SPOKES
      : Math.min(Math.round((clamped / 100) * SPOKES), SPOKES - 1);

  return (
    <div className="flex flex-col items-center gap-1 py-2">
      {/* The card's title covers both arcs, so each one has to name itself. */}
      <span className="mb-1 text-center font-display text-xs text-fg-muted">
        {item.label}
      </span>

      <div className="relative w-full max-w-[260px]">
        <svg
          viewBox="0 0 200 108"
          className="w-full"
          role="img"
          aria-label={`${item.label}: ${item.percent}% of ${format(item.target, item.value_format)}`}
        >
          {Array.from({ length: SPOKES }).map((_, index) => (
            <Spoke key={index} index={index} isFilled={index < filled} />
          ))}
        </svg>

        {/* The percentage sits in the arc's mouth. Absolutely positioned rather
            than an SVG <text>, so it inherits the page's font and scales with the
            type system instead of the viewBox. */}
        <span className="absolute inset-x-0 bottom-1 text-center font-display text-xs text-fg-muted">
          {item.percent}%
        </span>
      </div>

      {/* The scale sits immediately under the arc, and shares its max-width so the
          two figures land beneath the arc's own ends. These label the GAUGE — zero
          where it starts, the target where it fills — so they belong next to it.
          They were below the headline, which put the card's biggest number between
          an axis and the thing it measures. */}
      <div className="flex w-full max-w-[260px] items-center justify-between font-display text-xs text-fg-muted tabular-nums">
        <span>{format(0, item.value_format)}</span>
        <span>{format(item.target, item.value_format)}</span>
      </div>

      {/* The driver's OWN gross profit is the headline — where they actually are.
          The target is not repeated here; it is the right-hand end of the scale
          above.

          24px, the DS's `.buro-money` size — NOT the enlarged size the count tiles
          use. A tile has nothing in it but its figure, so the number can dominate;
          here it shares the card with the arc, and at 36px it overpowered the very
          thing it is supposed to be read against. */}
      <span className="mt-2 font-display text-2xl leading-[0.9] font-normal tabular-nums">
        {format(item.value, item.value_format)}
      </span>
    </div>
  );
}

/** A point on the arc, in SVG coordinates (y grows downward). */
function polar(radius: number, degrees: number): [number, number] {
  const radians = (degrees * Math.PI) / 180;

  return [
    CENTRE_X + radius * Math.cos(radians),
    CENTRE_Y - radius * Math.sin(radians),
  ];
}

/**
 * One tapered wedge of the arc.
 *
 * The edges are drawn straight rather than curved: over 11° the difference
 * between a chord and its arc is well under half a unit here, invisible at any
 * real size, and it keeps this to four points instead of arc flags that are easy
 * to get backwards.
 */
function Spoke({ index, isFilled }: { index: number; isFilled: boolean }) {
  // The span is inset by half a spoke at each end, so the arc runs from
  // (180 - H) to H rather than a full 180 -> 0.
  //
  // That inset is what makes the BOTTOM of the gauge a straight line. Centre the
  // end spokes on the horizontal instead and half of each one sits below it — and
  // because a spoke tapers, its lower edge slopes away as it goes outward, so the
  // baseline came out visibly crooked. Inset like this, the outermost spoke's lower
  // edge lies exactly along the horizontal and both of its caps are tangent to it.
  const centre =
    180 - SPOKE_HALF_ANGLE - (index / (SPOKES - 1)) * (180 - 2 * SPOKE_HALF_ANGLE);
  const from = centre + SPOKE_HALF_ANGLE;
  const to = centre - SPOKE_HALF_ANGLE;

  const [x1, y1] = polar(INNER_R, from);
  const [x2, y2] = polar(OUTER_R, from);
  const [x3, y3] = polar(OUTER_R, to);
  const [x4, y4] = polar(INNER_R, to);

  // Cap centres sit on the middle of each end edge.
  const [innerCx, innerCy] = polar(INNER_R, centre);
  const [outerCx, outerCy] = polar(OUTER_R, centre);

  return (
    // The fill lives on the group so the body and both caps inherit it — they have
    // to be one colour or the seams between them would show.
    //
    // Progress is pale DS mint in light, but `mint` CANNOT be used in dark: this
    // app remaps --brand-mint to rgb(52,55,62) there, which is the very same colour
    // as --folder-opened (#34373e) behind --widget-chip. The two states came out
    // byte-identical and the whole arc read as empty.
    //
    // --foreground-mint is the token for this: globals.css defines it as the accent
    // that stays legible in BOTH themes precisely because `mint` is a pale fill
    // that goes grey in dark.
    //
    // The EMPTY state goes the other way in dark. --widget-chip resolves to
    // --folder-opened (#34373e) there, five values off the card it sits on
    // (rgb(39,41,47)), so the unfilled part of the arc all but vanished into the
    // surface and the gauge read as though it stopped at the last mint segment.
    // Light has no such problem — the DS chip fill is #f1f1f1 on a near-white card
    // and separates cleanly — so only dark is overridden, and it darkens rather
    // than lightens: a well cut into the card, which is also what the DS's
    // recessed chips do.
    //
    // The alpha rides on the GROUP (`opacity`), never on the fill. A translucent
    // FILL is applied per shape, and each segment is three overlapping shapes —
    // body plus two caps — so every overlap composited twice and left a darker
    // blob at each end of every empty segment. Group opacity renders the three
    // into one buffer first and fades that, so the segment is a single flat tone.
    <g
      className={
        isFilled
          ? "fill-mint dark:fill-foreground-mint"
          // Empty segments are the same ink in both themes, only the strength
          // differs: widget-chip is near-white in light mode, which left the
          // empty half of the gauge invisible against the card.
          : "fill-black opacity-[0.14] dark:opacity-40"
      }
    >
      <path d={`M${x1} ${y1} L${x2} ${y2} L${x3} ${y3} L${x4} ${y4} Z`} />
      <circle cx={innerCx} cy={innerCy} r={capRadius(INNER_R)} />
      <circle cx={outerCx} cy={outerCy} r={capRadius(OUTER_R)} />
    </g>
  );
}
